/**
 * Task status and delay, shared by the task tracker and the editor report.
 *
 * Both screens carried their own copy of the same inline logic, and both
 * copies had the same three defects:
 *
 *   - TASK-01: an unfinished task on or past its deadline was shown as
 *     "Closed", hiding exactly the backlog a manager needs to act on.
 *   - TASK-02: the comparison was a strict isBefore, so finishing *on* the
 *     deadline day was reported as "Overdue".
 *   - TASK-03: an unfinished task's delay was hard-coded to 0, however late.
 *
 * A task carries two independent facts: completionDate (the work was done)
 * and ended (a manager closed it, which removes it from the pending list).
 * "Closed" now means only the second without the first.
 *
 * Dates. Deadlines and completion dates come from <input type="date"> and are
 * stored as UTC midnight. Reading them in local time happens to give the right
 * day east of UTC but the previous day west of it, so stored dates are read as
 * their UTC calendar day, and "today" is the viewer's local calendar day.
 * Deadlines are inclusive: work finished on the deadline day is on time.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const TASK_STATUS = Object.freeze({
  COMPLETED: "Completed",
  COMPLETED_LATE: "Completed Late",
  IN_PROGRESS: "In Progress",
  OVERDUE: "Overdue",
  CLOSED: "Closed",
});

/** Calendar day of a stored date-only value, as a UTC timestamp, or null. */
export function storedDay(value) {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** The viewer's local calendar day, on the same scale as storedDay. */
export function localDay(now = new Date()) {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

export function taskStatus(task, now = new Date()) {
  const deadline = storedDay(task?.deadlineDate);
  const completed = storedDay(task?.completionDate);

  if (completed !== null) {
    if (deadline === null || completed <= deadline) return TASK_STATUS.COMPLETED;
    return TASK_STATUS.COMPLETED_LATE;
  }

  if (task?.ended) return TASK_STATUS.CLOSED;
  if (deadline === null) return TASK_STATUS.IN_PROGRESS;

  return localDay(now) <= deadline ? TASK_STATUS.IN_PROGRESS : TASK_STATUS.OVERDUE;
}

/**
 * Days late, or null when it cannot be known.
 *
 * Completed work is frozen against its completion date. Open work is aged
 * against today, so the figure keeps growing until someone acts. A task closed
 * without being completed has no closure date recorded, so its delay is
 * unknown rather than zero.
 */
export function taskDelayDays(task, now = new Date()) {
  const deadline = storedDay(task?.deadlineDate);
  if (deadline === null) return null;

  const completed = storedDay(task?.completionDate);
  if (completed !== null) return Math.max(0, Math.round((completed - deadline) / DAY_MS));

  if (task?.ended) return null;
  return Math.max(0, Math.round((localDay(now) - deadline) / DAY_MS));
}

/** Delay formatted for a table cell. */
export function formatDelay(task, now = new Date()) {
  const days = taskDelayDays(task, now);
  return days === null ? "—" : days;
}
